import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
    FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Switch, FormControlLabel,
    Checkbox, Alert
} from '@mui/material';
import { LeaveTypeService } from '../../services/leave-type.service';
import { AccrualRule, DocumentRequirement, LeaveTypeConfig } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';

const DOC_REQUIREMENT_LABELS: Record<DocumentRequirement, string> = {
    [DocumentRequirement.NONE]: 'None',
    [DocumentRequirement.OPTIONAL]: 'Optional',
    [DocumentRequirement.MANDATORY]: 'Mandatory',
};

const ACCRUAL_RULE_LABELS: Record<AccrualRule, string> = {
    [AccrualRule.NONE]: 'None (policy grant)',
    [AccrualRule.PER_DAYS_WORKED]: 'Per days worked',
};

// Fix round 1 / F3, F4: UpdateLeaveTypeAccrualSettingsRequest's two numeric fields are
// @NotNull + bounded server-side (task-5-report). A `''` (or out-of-bounds) value must never
// reach the PATCH body — Save is disabled instead, matching the "disable submit out of bounds"
// ruling.
const DAYS_PER_UNIT_MIN = 1;
const DAYS_PER_UNIT_MAX = 366;
const CAP_MIN = 0;
const CAP_MAX = 365;
const DEFAULT_DAYS_PER_UNIT = 18;
const DEFAULT_CAP = 40;
const isValidDaysPerUnit = (v: number | ''): v is number =>
    v !== '' && Number.isInteger(v) && v >= DAYS_PER_UNIT_MIN && v <= DAYS_PER_UNIT_MAX;
const isValidCap = (v: number | ''): v is number =>
    v !== '' && Number.isInteger(v) && v >= CAP_MIN && v <= CAP_MAX;

const LeaveTypeManagement: React.FC = () => {
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [name, setName] = useState('');
    const [documentRequirement, setDocumentRequirement] = useState<DocumentRequirement>(DocumentRequirement.NONE);
    const [isPaid, setIsPaid] = useState<boolean>(true);
    const [encashable, setEncashable] = useState<boolean>(false);
    const [minimumTenureMonths, setMinimumTenureMonths] = useState<number | ''>('');
    const [attendanceBonusExempt, setAttendanceBonusExempt] = useState<boolean>(false);
    // Fix round 1 / M-3: an uncommitted per-row draft of the tenure-months TextField. Typing only
    // updates this map — it does NOT PATCH on every keystroke (the old behavior persisted every
    // intermediate value, e.g. "1" then "12" when editing 6 -> 12, and could leave a stale value
    // on the row if two requests landed out of order). The draft commits on blur; a row with no
    // entry here just renders the server value.
    const [tenureDrafts, setTenureDrafts] = useState<Record<string, string>>({});
    const [accrualDialogOpen, setAccrualDialogOpen] = useState(false);
    const [accrualEditingType, setAccrualEditingType] = useState<LeaveTypeConfig | null>(null);
    const [accrualRule, setAccrualRule] = useState<AccrualRule>(AccrualRule.NONE);
    const [accrualDaysPerUnit, setAccrualDaysPerUnit] = useState<number | ''>(18);
    const [carryForwardCapDays, setCarryForwardCapDays] = useState<number | ''>(40);
    const toast = useToast();

    useEffect(() => {
        fetchLeaveTypes();
    }, []);

    const fetchLeaveTypes = async () => {
        try {
            const data = await LeaveTypeService.getByInstitution();
            setLeaveTypes(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave types');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!name.trim()) return;
        try {
            await LeaveTypeService.create(
                name.trim(), documentRequirement, isPaid, encashable,
                minimumTenureMonths === '' ? null : minimumTenureMonths, attendanceBonusExempt
            );
            setDialogOpen(false);
            setName('');
            setDocumentRequirement(DocumentRequirement.NONE);
            setIsPaid(true);
            setEncashable(false);
            setMinimumTenureMonths('');
            setAttendanceBonusExempt(false);
            await fetchLeaveTypes();
            toast.showSuccess('Leave type created');
        } catch (err) {
            toast.showApiError(err, 'Failed to create leave type');
        }
    };

    // Fix round 1 / M-3: called on blur, not on every keystroke. Commits the row's draft (if it
    // differs from the last known server value), re-syncs the row from the ACTUAL response
    // (rather than trusting the locally-typed value), and always clears the draft afterward —
    // on success that reveals the fresh server value, on failure it reveals the last-known
    // (unchanged) server value instead of leaving a stale, never-saved draft on screen.
    const commitMinimumTenureMonths = async (lt: LeaveTypeConfig) => {
        const draft = tenureDrafts[lt.id];
        if (draft === undefined) return;
        const value = draft === '' ? null : Number(draft);
        if (draft !== '' && !Number.isFinite(value)) {
            setTenureDrafts(prev => {
                const next = { ...prev };
                delete next[lt.id];
                return next;
            });
            return;
        }
        const previousValue = lt.minimumTenureMonths ?? null;
        if (value === previousValue) {
            setTenureDrafts(prev => {
                const next = { ...prev };
                delete next[lt.id];
                return next;
            });
            return;
        }
        try {
            const updated = await LeaveTypeService.updateMinimumTenureMonths(lt.id, value);
            setLeaveTypes(prev => prev.map(x =>
                x.id === lt.id ? { ...x, minimumTenureMonths: updated.minimumTenureMonths ?? undefined } : x
            ));
            toast.showSuccess('Minimum tenure updated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update minimum tenure');
        } finally {
            setTenureDrafts(prev => {
                const next = { ...prev };
                delete next[lt.id];
                return next;
            });
        }
    };

    const handleAttendanceBonusExemptChange = async (id: string, checked: boolean) => {
        try {
            await LeaveTypeService.updateAttendanceBonusExempt(id, checked);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, attendanceBonusExempt: checked } : lt
            ));
            toast.showSuccess('Attendance bonus exemption updated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update attendance bonus exemption');
        }
    };

    const handleEncashableChange = async (id: string, checked: boolean) => {
        try {
            await LeaveTypeService.updateEncashableStatus(id, checked);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, encashable: checked } : lt
            ));
            // Spec §2.2 M1: the run's type-name lookup is soft-delete-/isActive-blind, so
            // encashments already created for this type stay payable in the next run even
            // after this flag is turned off — only NEW encashments are blocked. Say so on the
            // off-transition; the on-transition needs no such caveat.
            toast.showSuccess(
                checked
                    ? 'Encashable status updated'
                    : 'Encashable status updated. New encashments can no longer be created for '
                      + 'this type. Encashments already created for it stay payable in the next payroll run.'
            );
        } catch (err) {
            toast.showApiError(err, 'Failed to update encashable status');
        }
    };

    const handlePaidStatusChange = async (id: string, checked: boolean) => {
        try {
            await LeaveTypeService.updatePaidStatus(id, checked);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, isPaid: checked } : lt
            ));
            toast.showSuccess('Paid status updated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update paid status');
        }
    };

    const handleDocRequirementChange = async (id: string, value: DocumentRequirement) => {
        try {
            await LeaveTypeService.updateDocumentRequirement(id, value);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, documentRequirement: value } : lt
            ));
            toast.showSuccess('Document requirement updated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update document requirement');
        }
    };

    const openAccrualDialog = (lt: LeaveTypeConfig) => {
        setAccrualEditingType(lt);
        setAccrualRule(lt.accrualRule ?? AccrualRule.NONE);
        setAccrualDaysPerUnit(lt.accrualDaysPerUnit ?? 18);
        setCarryForwardCapDays(lt.carryForwardCapDays ?? 40);
        setAccrualDialogOpen(true);
    };

    // Fix round 1 / F3: the server requires all three fields non-null, so an emptied/invalid
    // field must never be submitted. While PER_DAYS_WORKED, Save is disabled unless both fields
    // are valid (see isAccrualFormValid below), so the cast here is safe. While NONE, the fields
    // are disabled/uneditable in the UI but may still hold a stale '' from an earlier edit — fall
    // back to the house defaults rather than posting null.
    const isAccrualFormValid =
        accrualRule === AccrualRule.NONE ||
        (isValidDaysPerUnit(accrualDaysPerUnit) && isValidCap(carryForwardCapDays));

    const handleSaveAccrualSettings = async () => {
        if (!accrualEditingType || !isAccrualFormValid) return;
        try {
            const updated = await LeaveTypeService.updateAccrualSettings(accrualEditingType.id, {
                accrualRule,
                accrualDaysPerUnit: isValidDaysPerUnit(accrualDaysPerUnit)
                    ? accrualDaysPerUnit : DEFAULT_DAYS_PER_UNIT,
                carryForwardCapDays: isValidCap(carryForwardCapDays)
                    ? carryForwardCapDays : DEFAULT_CAP,
            });
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === accrualEditingType.id
                    ? { ...lt, accrualRule: updated.accrualRule, accrualDaysPerUnit: updated.accrualDaysPerUnit, carryForwardCapDays: updated.carryForwardCapDays }
                    : lt
            ));
            setAccrualDialogOpen(false);
            setAccrualEditingType(null);
            toast.showSuccess('Accrual settings updated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update accrual settings');
        }
    };

    const handleToggleActive = async (id: string, currentlyActive: boolean) => {
        const nextActive = !currentlyActive;
        const verb = nextActive ? 'reactivate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${verb} this leave type?`)) return;
        try {
            await LeaveTypeService.setActiveStatus(id, nextActive);
            setLeaveTypes(prev => prev.map(lt =>
                lt.id === id ? { ...lt, isActive: nextActive } : lt
            ));
            toast.showSuccess(nextActive ? 'Leave type reactivated' : 'Leave type deactivated');
        } catch (err) {
            toast.showApiError(err, `Failed to ${verb} leave type`);
        }
    };

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box data-help-page="leave.types.page" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>Leave Type Management</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                <Button data-help="leave.types.add" variant="contained" onClick={() => { setName(''); setDocumentRequirement(DocumentRequirement.NONE); setIsPaid(true); setEncashable(false); setMinimumTenureMonths(''); setAttendanceBonusExempt(false); setDialogOpen(true); }}>
                    Add Leave Type
                </Button>
            </Box>

            <TableContainer data-help-section="leave.types.table" component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="leave.types.col.name">Name</TableCell>
                            <TableCell data-help="leave.types.col.document">Document Requirement</TableCell>
                            <TableCell data-help="leave.types.col.paid">Paid</TableCell>
                            <TableCell data-help="leave.types.col.encashable">Encashable</TableCell>
                            <TableCell data-help="leave.types.col.tenure">Min. Tenure (months)</TableCell>
                            <TableCell data-help="leave.types.col.bonusExempt">Bonus Exempt</TableCell>
                            <TableCell data-help="leave.types.col.accrual">Accrual</TableCell>
                            <TableCell data-help="leave.types.col.status">Status</TableCell>
                            <TableCell data-help="leave.types.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaveTypes.map((lt) => {
                            const active = lt.isActive !== false;
                            return (
                            <TableRow key={lt.id} sx={{ opacity: active ? 1 : 0.55 }}>
                                <TableCell>{lt.name}</TableCell>
                                <TableCell>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <Select
                                            data-help="leave.types.docRequirement"
                                            value={lt.documentRequirement || DocumentRequirement.NONE}
                                            onChange={(e: SelectChangeEvent) =>
                                                handleDocRequirementChange(lt.id, e.target.value as DocumentRequirement)
                                            }
                                        >
                                            {Object.values(DocumentRequirement).map((req) => (
                                                <MenuItem key={req} value={req}>
                                                    {DOC_REQUIREMENT_LABELS[req]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        data-help="leave.types.paid"
                                        checked={lt.isPaid ?? true}
                                        onChange={(e) => handlePaidStatusChange(lt.id, e.target.checked)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        data-help="leave.types.encashable"
                                        checked={lt.encashable ?? false}
                                        onChange={(e) => handleEncashableChange(lt.id, e.target.checked)}
                                        size="small"
                                        inputProps={{ 'aria-label': `Encashable — ${lt.name}` }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        data-help="leave.types.tenure"
                                        type="number"
                                        size="small"
                                        sx={{ width: 90 }}
                                        value={tenureDrafts[lt.id] ?? (lt.minimumTenureMonths ?? '')}
                                        placeholder="None"
                                        inputProps={{ min: 0, max: 120, 'aria-label': `Minimum tenure months — ${lt.name}` }}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            setTenureDrafts(prev => ({ ...prev, [lt.id]: raw }));
                                        }}
                                        onBlur={() => commitMinimumTenureMonths(lt)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        data-help="leave.types.bonusExempt"
                                        checked={lt.attendanceBonusExempt ?? false}
                                        onChange={(e) => handleAttendanceBonusExemptChange(lt.id, e.target.checked)}
                                        size="small"
                                        inputProps={{ 'aria-label': `Attendance bonus exempt — ${lt.name}` }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={ACCRUAL_RULE_LABELS[lt.accrualRule ?? AccrualRule.NONE]}
                                        size="small"
                                        color={(lt.accrualRule ?? AccrualRule.NONE) === AccrualRule.NONE ? 'default' : 'primary'}
                                        variant={(lt.accrualRule ?? AccrualRule.NONE) === AccrualRule.NONE ? 'outlined' : 'filled'}
                                        sx={{ mr: 1 }}
                                    />
                                    <Button
                                        size="small"
                                        data-help="leave.types.action.editAccrual"
                                        onClick={() => openAccrualDialog(lt)}
                                        aria-label={`Edit accrual settings — ${lt.name}`}
                                    >
                                        Edit
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={active ? 'Active' : 'Inactive'}
                                        color={active ? 'success' : 'default'}
                                        size="small"
                                        variant={active ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        size="small"
                                        color={active ? 'warning' : 'success'}
                                        data-help="leave.types.action.toggleActive"
                                        onClick={() => handleToggleActive(lt.id, active)}
                                    >
                                        {active ? 'Deactivate' : 'Reactivate'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                        {leaveTypes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">No leave types configured</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ 'data-help-page': 'leave.typeForm.page' } as any} maxWidth="xs" fullWidth>
                <DialogTitle>Add Leave Type</DialogTitle>
                <DialogContent data-help-section="leave.typeForm.section.details">
                    <TextField
                        autoFocus
                        data-help="leave.typeForm.field.name"
                        label="Leave Type Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="doc-req-label">Document Requirement</InputLabel>
                        <Select
                            labelId="doc-req-label"
                            data-help="leave.typeForm.field.docRequirement"
                            value={documentRequirement}
                            label="Document Requirement"
                            onChange={(e: SelectChangeEvent) =>
                                setDocumentRequirement(e.target.value as DocumentRequirement)
                            }
                        >
                            {Object.values(DocumentRequirement).map((req) => (
                                <MenuItem key={req} value={req}>
                                    {DOC_REQUIREMENT_LABELS[req]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        data-help="leave.typeForm.field.paid"
                        control={
                            <Checkbox
                                checked={isPaid}
                                onChange={(e) => setIsPaid(e.target.checked)}
                            />
                        }
                        label="Paid leave"
                        sx={{ mt: 1 }}
                    />
                    <FormControlLabel
                        data-help="leave.typeForm.field.encashable"
                        control={
                            <Switch
                                checked={encashable}
                                onChange={(e) => setEncashable(e.target.checked)}
                            />
                        }
                        label="Encashable (eligible for EL encashment / settlement)"
                        sx={{ mt: 1, display: 'block' }}
                    />
                    <TextField
                        data-help="leave.typeForm.field.tenure"
                        label="Minimum tenure (months)"
                        type="number"
                        fullWidth
                        value={minimumTenureMonths}
                        onChange={(e) => setMinimumTenureMonths(e.target.value === '' ? '' : Number(e.target.value))}
                        inputProps={{ min: 0, max: 120 }}
                        helperText="Leave blank for no tenure gate (e.g. Maternity Leave uses 6)"
                        sx={{ mt: 2 }}
                    />
                    <FormControlLabel
                        data-help="leave.typeForm.field.bonusExempt"
                        control={
                            <Switch
                                checked={attendanceBonusExempt}
                                onChange={(e) => setAttendanceBonusExempt(e.target.checked)}
                            />
                        }
                        label="Attendance-bonus exempt (approved days don't forfeit the attendance bonus)"
                        sx={{ mt: 1, display: 'block' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.typeForm.cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button data-help="leave.typeForm.save" variant="contained" onClick={handleCreate} disabled={!name.trim()}>Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={accrualDialogOpen}
                PaperProps={{ 'data-help-page': 'leave.accrualForm.page' } as any}
                onClose={() => setAccrualDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Accrual settings{accrualEditingType ? ` — ${accrualEditingType.name}` : ''}
                </DialogTitle>
                <DialogContent data-help-section="leave.accrualForm.section.details">
                    <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                        <InputLabel id="accrual-rule-label">Accrual rule</InputLabel>
                        <Select
                            labelId="accrual-rule-label"
                            data-help="leave.accrualForm.field.rule"
                            label="Accrual rule"
                            value={accrualRule}
                            onChange={(e: SelectChangeEvent) => setAccrualRule(e.target.value as AccrualRule)}
                        >
                            {Object.values(AccrualRule).map((rule) => (
                                <MenuItem key={rule} value={rule}>{ACCRUAL_RULE_LABELS[rule]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {accrualRule === AccrualRule.PER_DAYS_WORKED && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Enabling accrual REPLACES this leave type's policy-based grant — the
                            employee's allocated days become the sum of accrued days instead of the
                            policy number.
                        </Alert>
                    )}

                    <TextField
                        data-help="leave.accrualForm.field.daysPerUnit"
                        label="Days accrued per unit worked"
                        type="number"
                        fullWidth
                        value={accrualDaysPerUnit}
                        onChange={(e) => setAccrualDaysPerUnit(e.target.value === '' ? '' : Number(e.target.value))}
                        disabled={accrualRule === AccrualRule.NONE}
                        inputProps={{ min: DAYS_PER_UNIT_MIN, max: DAYS_PER_UNIT_MAX }}
                        error={accrualRule === AccrualRule.PER_DAYS_WORKED && !isValidDaysPerUnit(accrualDaysPerUnit)}
                        helperText={
                            accrualRule === AccrualRule.PER_DAYS_WORKED && !isValidDaysPerUnit(accrualDaysPerUnit)
                                ? `${DAYS_PER_UNIT_MIN}-${DAYS_PER_UNIT_MAX}`
                                : ' '
                        }
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        data-help="leave.accrualForm.field.carryCap"
                        label="Carry-forward cap (days)"
                        type="number"
                        fullWidth
                        value={carryForwardCapDays}
                        onChange={(e) => setCarryForwardCapDays(e.target.value === '' ? '' : Number(e.target.value))}
                        disabled={accrualRule === AccrualRule.NONE}
                        inputProps={{ min: CAP_MIN, max: CAP_MAX }}
                        error={accrualRule === AccrualRule.PER_DAYS_WORKED && !isValidCap(carryForwardCapDays)}
                        helperText={
                            accrualRule === AccrualRule.PER_DAYS_WORKED && !isValidCap(carryForwardCapDays)
                                ? `${CAP_MIN}-${CAP_MAX}`
                                // Fix round 1 / F12: only claim "carries nothing" when the rule
                                // that would use this cap is actually enabled.
                                : accrualRule === AccrualRule.PER_DAYS_WORKED && carryForwardCapDays === 0
                                    ? 'A cap of 0 carries nothing forward.'
                                    : ' '
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.accrualForm.cancel" onClick={() => setAccrualDialogOpen(false)}>Cancel</Button>
                    <Button data-help="leave.accrualForm.save" variant="contained" onClick={handleSaveAccrualSettings} disabled={!isAccrualFormValid}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveTypeManagement;
